import {
    Avatar,
    Box,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    Paper,
    Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Area, AreaChart, ReferenceLine, ResponsiveContainer, YAxis } from 'recharts';
import { Individual } from '../../../utils';
import {
    loadDistanceBySpeedData,
    loadHighSpeedSprintsData,
    loadSessionData,
    loadSprintData,
} from '../api/kpi-queries';
import { fetchSignedUrl, getRawSessionData } from '../api/performance.api';
import { aggregateData } from '../community.func';
import SpeedManagementPlayerMetricsGrid from './SpeedManagementPlayerMetricsGrid';
import { useSpeedUnitPreference } from 'hooks/useSpeedUnitPreference';

interface SpeedManagementIndividualCardProps {
    individual: Individual;
}

const SpeedManagementIndividualCard = ({ individual }: SpeedManagementIndividualCardProps) => {
    const { push } = useHistory();
    const { convert, label: speedLabel } = useSpeedUnitPreference();

    // Individual state variables for each metric
    const [maxSpeed, setMaxSpeed] = useState<number | null>(null);

    // Sprint metrics
    const [sprintsLoading, setSprintsLoading] = useState(false);
    const [sprints7Days, setSprints7Days] = useState<number | null>(null);
    const [sprints30Days, setSprints30Days] = useState<number | null>(null);

    // High speed sprints metrics
    const [highSpeedSprintsLoading, setHighSpeedSprintsLoading] = useState(false);
    const [highSpeedSprints7Days, setHighSpeedSprints7Days] = useState<number | null>(null);
    const [highSpeedSprints30Days, setHighSpeedSprints30Days] = useState<number | null>(null);

    // Session metrics
    const [sessionLoading, setSessionLoading] = useState(false);
    const [distance7Days, setDistance7Days] = useState<number | null>(null);
    const [distance30Days, setDistance30Days] = useState<number | null>(null);
    const [load7Days, setLoad7Days] = useState<number | null>(null);
    const [load30Days, setLoad30Days] = useState<number | null>(null);
    const [accels7Days, setAccels7Days] = useState<number | null>(null);
    const [accels30Days, setAccels30Days] = useState<number | null>(null);
    const [decels7Days, setDecels7Days] = useState<number | null>(null);
    const [decels30Days, setDecels30Days] = useState<number | null>(null);

    // High speed distance metrics
    const [highSpeedDistanceLoading, setHighSpeedDistanceLoading] = useState(false);
    const [highSpeedDistanceEighty7Days, setHighSpeedDistanceEighty7Days] = useState<number | null>(
        null,
    );
    const [highSpeedDistanceEighty30Days, setHighSpeedDistanceEighty30Days] = useState<
        number | null
    >(null);
    const [highSpeedDistanceEightyFive7Days, setHighSpeedDistanceEightyFive7Days] = useState<
        number | null
    >(null);
    const [highSpeedDistanceEightyFive30Days, setHighSpeedDistanceEightyFive30Days] = useState<
        number | null
    >(null);

    // Raw session data
    const [rawDataLoading, setRawDataLoading] = useState(false);
    const [rawDataBodies, setRawDataBodies] = useState<
        Array<{
            data: Array<{ t: number; s: number }>;
            uuid: string;
            activity_date: string;
            individual_uuid: string;
        }>
    >([]);

    // Load sprint data
    useEffect(() => {
        if (!individual) return;

        const loadSprintMetrics = async () => {
            setSprintsLoading(true);
            try {
                const response = await loadSprintData(individual.uuid);
                const responseTable = response.tablePivot({
                    fillMissingDates: true,
                });

                const speedAggregatedData = aggregateData(
                    responseTable,
                    'FactSprintsRanked.vMaxMaxMph',
                    'max',
                );

                const sprintsAggregatedData = aggregateData(
                    responseTable,
                    'FactSprintsRanked.count',
                    'sum',
                );

                setMaxSpeed(speedAggregatedData.last180Days);
                setSprints7Days(sprintsAggregatedData.last7Days);
                setSprints30Days(sprintsAggregatedData.last30Days);
            } catch (error) {
                console.error('Error loading sprint data:', error);
            } finally {
                setSprintsLoading(false);
            }
        };

        loadSprintMetrics();
    }, [individual]);

    // Load session data
    useEffect(() => {
        if (!individual) return;

        const loadSessionMetrics = async () => {
            setSessionLoading(true);
            try {
                const sessionResponse = await loadSessionData(individual.uuid);
                const sessionResponseTable = sessionResponse.tablePivot({
                    fillMissingDates: true,
                });

                const distanceAggregatedData = aggregateData(
                    sessionResponseTable,
                    'FactUploads.totalDistanceYd',
                    'sum',
                );

                const loadAggregatedData = aggregateData(
                    sessionResponseTable,
                    'FactUploads.totalPlayerLoad',
                    'sum',
                );

                const accelAggregatedData = aggregateData(
                    sessionResponseTable,
                    'FactUploads.totalAccelerations',
                    'sum',
                );

                const decelAggregatedData = aggregateData(
                    sessionResponseTable,
                    'FactUploads.totalDecelerations',
                    'sum',
                );

                setDistance7Days(distanceAggregatedData.last7Days);
                setDistance30Days(distanceAggregatedData.last30Days);
                setLoad7Days(loadAggregatedData.last7Days);
                setLoad30Days(loadAggregatedData.last30Days);
                setAccels7Days(accelAggregatedData.last7Days);
                setAccels30Days(accelAggregatedData.last30Days);
                setDecels7Days(decelAggregatedData.last7Days);
                setDecels30Days(decelAggregatedData.last30Days);
            } catch (error) {
                console.error('Error loading session data:', error);
            } finally {
                setSessionLoading(false);
            }
        };

        loadSessionMetrics();
    }, [individual]);

    // Load high speed sprints data
    useEffect(() => {
        if (!individual) return;

        const loadHighSpeedSprintsMetrics = async () => {
            setHighSpeedSprintsLoading(true);
            try {
                const highSpeedSprintsResponse = await loadHighSpeedSprintsData(individual.uuid);
                const highSpeedSprintsTable = highSpeedSprintsResponse.tablePivot({
                    fillMissingDates: true,
                });

                const highSpeedSprintsAggregatedData = aggregateData(
                    highSpeedSprintsTable,
                    'FactSprintsRanked.count',
                    'sum',
                );

                setHighSpeedSprints7Days(highSpeedSprintsAggregatedData.last7Days);
                setHighSpeedSprints30Days(highSpeedSprintsAggregatedData.last30Days);
            } catch (error) {
                console.error('Error loading high speed sprints data:', error);
            } finally {
                setHighSpeedSprintsLoading(false);
            }
        };

        loadHighSpeedSprintsMetrics();
    }, [individual]);

    // Load high speed distance data
    useEffect(() => {
        if (!individual || !maxSpeed) return;

        const loadHighSpeedDistanceMetrics = async () => {
            setHighSpeedDistanceLoading(true);
            try {
                // Load distance at 80% max speed
                const distanceBySpeedResponseEighty = await loadDistanceBySpeedData(
                    individual.uuid,
                    maxSpeed * 0.8,
                );

                const distanceBySpeedEightyTable = distanceBySpeedResponseEighty.tablePivot({
                    fillMissingDates: true,
                });

                const distanceBySpeedAggregatedDataEighty = aggregateData(
                    distanceBySpeedEightyTable,
                    'FactGpsDistances.totalDistance',
                    'sum',
                );

                // Load distance at 85% max speed
                const distanceBySpeedResponseEightyFive = await loadDistanceBySpeedData(
                    individual.uuid,
                    maxSpeed * 0.85,
                );

                const distanceBySpeedEightyFiveTable = distanceBySpeedResponseEightyFive.tablePivot(
                    {
                        fillMissingDates: true,
                    },
                );

                const distanceBySpeedAggregatedDataEightyFive = aggregateData(
                    distanceBySpeedEightyFiveTable,
                    'FactGpsDistances.totalDistance',
                    'sum',
                );

                setHighSpeedDistanceEighty7Days(distanceBySpeedAggregatedDataEighty.last7Days);
                setHighSpeedDistanceEighty30Days(distanceBySpeedAggregatedDataEighty.last30Days);
                setHighSpeedDistanceEightyFive7Days(
                    distanceBySpeedAggregatedDataEightyFive.last7Days,
                );
                setHighSpeedDistanceEightyFive30Days(
                    distanceBySpeedAggregatedDataEightyFive.last30Days,
                );
            } catch (error) {
                console.error('Error loading high speed distance data:', error);
            } finally {
                setHighSpeedDistanceLoading(false);
            }
        };

        loadHighSpeedDistanceMetrics();
    }, [individual, maxSpeed]);

    // Load raw session data
    useEffect(() => {
        if (!individual) return;

        const loadRawSessionData = async () => {
            setRawDataLoading(true);
            try {
                const rawSessionDataResponse = await getRawSessionData(individual.uuid);

                // Create array of empty objects with metadata
                const emptyRawDataBodies = rawSessionDataResponse.data.map((session) => ({
                    data: [],
                    uuid: session.uuid,
                    activity_date: session.activity_date,
                    individual_uuid: session.individual_uuid,
                }));

                setRawDataBodies(emptyRawDataBodies);

                // Process each URL individually
                rawSessionDataResponse.data.forEach(async (session, index) => {
                    try {
                        const result = await fetchSignedUrl(session.signed_url);

                        // Update just this specific session's data
                        setRawDataBodies((currentBodies) =>
                            currentBodies.map((body, i) =>
                                i === index ? { ...body, data: result.data } : body,
                            ),
                        );
                    } catch (error) {
                        console.error(`Error fetching data for session ${session.uuid}:`, error);
                    }
                });
            } catch (error) {
                console.error('Error loading raw session data:', error);
            } finally {
                setRawDataLoading(false);
            }
        };

        loadRawSessionData();
    }, [individual]);

    // Calculate overall loading state for the UI
    const isAnyLoading =
        sprintsLoading ||
        sessionLoading ||
        highSpeedSprintsLoading ||
        highSpeedDistanceLoading ||
        rawDataLoading;

    return (
        <Card
            sx={{
                maxHeight: 1200,
                display: 'flex',
                flexDirection: 'column',
                background: 'linear-gradient(180deg, #f2f2f2 0%, #dbdbdb 100%)',
            }}
        >
            <CardContent
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    padding: 2,
                }}
            >
                <CardHeader
                    avatar={
                        <Avatar
                            variant={'rounded'}
                            src={individual.image_urls?.avatar || ''}
                            alt={individual.name}
                            sx={{ width: 80, height: 80 }}
                        />
                    }
                    title={
                        <Typography
                            variant="h6"
                            component="div"
                            align="center"
                            sx={{
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                width: '100%',
                            }}
                        >
                            {individual.name}
                        </Typography>
                    }
                    sx={{
                        flexDirection: 'column',
                        alignItems: 'center',
                        '.MuiCardHeader-content': {
                            width: '100%',
                        },
                    }}
                />
                <Paper sx={{ padding: 5 }}>
                    <Typography variant="body2" color="text.secondary" align="center">
                        Max Speed Past 6 Months
                    </Typography>
                    <Typography variant="h5" component="div" align="center">
                        {sprintsLoading ? (
                            <CircularProgress />
                        ) : maxSpeed ? (
                            convert(maxSpeed).toFixed(2)
                        ) : (
                            '-'
                        )}{' '}
                        {speedLabel}
                    </Typography>
                </Paper>

                <Box sx={{ width: '100%' }}>
                    <Paper sx={{ margin: 5 }}>
                        <SpeedManagementPlayerMetricsGrid
                            loading={isAnyLoading}
                            title="Last 7 Days"
                            sprints={sprints7Days}
                            distance={distance7Days}
                            playerLoad={load7Days}
                            accelerations={accels7Days}
                            decelerations={decels7Days}
                            highSpeedSprints={highSpeedSprints7Days}
                            highSpeedDistanceEighty={highSpeedDistanceEighty7Days}
                            highSpeedDistanceEightyFive={highSpeedDistanceEightyFive7Days}
                        />
                    </Paper>
                    <Paper sx={{ margin: 5 }}>
                        <SpeedManagementPlayerMetricsGrid
                            loading={isAnyLoading}
                            title="Last 30 Days"
                            sprints={sprints30Days}
                            distance={distance30Days}
                            playerLoad={load30Days}
                            accelerations={accels30Days}
                            decelerations={decels30Days}
                            highSpeedSprints={highSpeedSprints30Days}
                            highSpeedDistanceEighty={highSpeedDistanceEighty30Days}
                            highSpeedDistanceEightyFive={highSpeedDistanceEightyFive30Days}
                        />
                    </Paper>
                    <Paper sx={{ margin: 5, padding: 1 }}>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            align="center"
                            sx={{ mb: 1 }}
                        >
                            Recent Speed Data
                        </Typography>
                        {rawDataLoading ? (
                            <CircularProgress />
                        ) : rawDataBodies.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" align="center">
                                No speed data available
                            </Typography>
                        ) : (
                            rawDataBodies.map((data, index) => (
                                <Box key={index} sx={{ width: '100%' }}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        component="div"
                                        sx={{
                                            cursor: 'pointer',
                                            '&:hover': { textDecoration: 'underline' },
                                        }}
                                        onClick={() => {
                                            push(
                                                `/community/individuals/${data.individual_uuid}/performance/${data.uuid}`,
                                            );
                                        }}
                                    >
                                        {DateTime.fromISO(data.activity_date, {
                                            zone: 'local',
                                        }).toLocaleString({})}
                                    </Typography>
                                    {
                                        <ResponsiveContainer width="100%" height={100}>
                                            <AreaChart data={data.data}>
                                                <YAxis
                                                    domain={
                                                        maxSpeed
                                                            ? [0, maxSpeed * 0.44704]
                                                            : undefined
                                                    }
                                                    hide
                                                />
                                                <Area
                                                    type="monotone"
                                                    dataKey="s"
                                                    stroke="#8884d8"
                                                    fill="#8884d8"
                                                    dot={false}
                                                    strokeWidth={1}
                                                />
                                                {maxSpeed && maxSpeed > 0 && (
                                                    <ReferenceLine
                                                        y={maxSpeed * 0.8 * 0.44704}
                                                        stroke="rgba(255,0,0,0.3)"
                                                        strokeDasharray="3 3"
                                                        label={{
                                                            value: '80% maxV',
                                                            position: 'insideRight',
                                                            fill: 'rgba(255,0,0,0.5)',
                                                            fontSize: 12,
                                                        }}
                                                    />
                                                )}
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    }
                                </Box>
                            ))
                        )}
                    </Paper>
                </Box>
            </CardContent>
        </Card>
    );
};

export default SpeedManagementIndividualCard;
